import { Flex, Form, Input } from 'antd';
import { Button } from '@/components/button/button';
import { useNavigate, useOutletContext } from 'react-router';
import { useCreateDocument } from '@/hooks/use-create-document/use-create-document';
import { CustomFilters } from '@/components/custom-filters/custom-filters';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useState, useCallback } from 'react';
import styles from './create-document.module.scss';

type DocumentsOutletContext = { companiesList: string[] };

export function CreateDocumentPage() {
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const { createDocument, isPending } = useCreateDocument();

  const { companiesList } = useOutletContext<DocumentsOutletContext>();
  const [selectedCompany, setSelectedCompany] = useState<string | undefined>(undefined);
  const [companyQuery, setCompanyQuery] = useState<string>('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companyQuery,
  });

  const nameValue = Form.useWatch('name', form);
  const contentValue = Form.useWatch('content', form);
  const isCreateDisabled =
    isPending ||
    !selectedCompany ||
    !String(nameValue || '').trim() ||
    !String(contentValue || '').trim();

  const handleCompanyChange = useCallback((updated: { company_name?: string }) => {
    const value = updated.company_name;
    setSelectedCompany(value);
    form.setFieldsValue({ company_name: value });
  }, []);

  const handleFinish = (values: { company_name: string; name: string; content: string }) => {
    createDocument(
      { company_name: values.company_name, name: values.name, content: values.content },
      {
        onSuccess: () => {
          navigate('/documents');
        },
      }
    );
  };

  return (
    <Flex vertical gap={16} style={{ width: '100%' }}>
      <Form form={form} layout="vertical" onFinish={handleFinish}>
        <Form.Item name="company_name" hidden>
          <Input />
        </Form.Item>

        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8 }}>Company</div>
          <CustomFilters
            customClassName={undefined}
            companiesList={companiesList || []}
            filters={{ company_name: selectedCompany }}
            defaultCompanyValue={undefined}
            onChangeFilters={handleCompanyChange}
            onSearchQueryChange={setCompanyQuery}
            searchResults={companySearchData}
            isSearchLoading={companySearchLoading}
            searchError={companySearchError as Error | null}
          />
        </div>

        <Form.Item label="Name" name="name">
          <Input placeholder="Document name" className={styles.documentNameInput} />
        </Form.Item>

        <Form.Item label="Content" name="content">
          <Input.TextArea
            className={styles.documentContentInput}
            rows={10}
            autoSize={{ minRows: 8, maxRows: 40 }}
            placeholder="Document content"
          />
        </Form.Item>
      </Form>

      <Flex gap={8} justify="end">
        <Button buttonVariant="secondary" onClick={() => navigate('/documents')}>
          Cancel
        </Button>
        <Button
          buttonVariant="primary"
          loading={isPending}
          disabled={isCreateDisabled}
          onClick={() => form.submit()}
        >
          Create
        </Button>
      </Flex>
    </Flex>
  );
}
